import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { AlertPill } from "../AlertPill";
import { useBannerAlerts } from "./use-banner-alerts";
import type { BannerAlertTone, IBannerAlert } from "./types";

/** Beyond two the hero stops being a banner and starts being an inbox. */
const MAX_VISIBLE = 2;

const TONE_BUTTON_VARIANT: Record<BannerAlertTone, "danger" | "warning" | "primary"> = {
  danger: "danger",
  warning: "warning",
  info: "primary",
};

/** `max-sm:flex-1` so the buttons split the row evenly once it wraps, rather than
 *  huddling at one edge with unreachable-looking touch targets. */
const ACTION_CLASSES = "h-7! rounded-xl px-2.5! text-[11px]! max-sm:flex-1";

const AlertActions = ({ alert }: { alert: IBannerAlert }) => (
  <>
    {alert.secondaryAction && (
      <Button asChild size="xs" variant="outline" className={ACTION_CLASSES}>
        <Link to={alert.secondaryAction.to}>{alert.secondaryAction.label}</Link>
      </Button>
    )}
    {alert.action && (
      <Button
        asChild
        size="xs"
        variant={TONE_BUTTON_VARIANT[alert.tone]}
        className={ACTION_CLASSES}
      >
        <Link to={alert.action.to}>{alert.action.label}</Link>
      </Button>
    )}
  </>
);

interface Props {
  className?: React.HTMLAttributes<HTMLElement>["className"];
}

export const BannerAlerts = ({ className }: Props) => {
  const { t } = useTranslation("my-space");
  const alerts = useBannerAlerts();
  const [showAll, setShowAll] = useState(false);

  if (alerts.length === 0) return null;

  const visible = showAll ? alerts : alerts.slice(0, MAX_VISIBLE);
  const hiddenCount = alerts.length - visible.length;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {visible.map((alert) => (
        <AlertPill
          key={alert.id}
          tone={alert.tone}
          icon={alert.icon}
          title={alert.title}
          description={alert.description}
          actions={<AlertActions alert={alert} />}
        />
      ))}

      {/* Expands in place — there is no unified "all alerts" destination to link to. */}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="self-center rounded-lg px-2 py-0.5 text-[11px] font-medium text-primary-strong/70 underline-offset-2 hover:text-primary-strong hover:underline focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-hidden"
        >
          {t("dashboard.more_alerts", { count: hiddenCount })}
        </button>
      )}
    </div>
  );
};
